import { cloneDeep } from "lodash";
import { axios } from "../api";
import type { IAircraftDetail } from "../aircraft/types";
import { renderTemplatePreviewBlob } from "../reports-and-forms/reports-and-forms";
import type { IJob } from "../workorder/types";
import type { InvoiceDetails, InvoiceLines } from "./types";

export interface InvoicePdfV2BuildResult {
  blob: Blob;
  filename: string;
}

function formatAircraftTotalTime(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0:00";
  const totalMinutes = Math.round(n * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.abs(totalMinutes % 60);
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

function resolveAircraftModel(aircraft: IAircraftDetail | null): string {
  if (!aircraft) return "";
  return (
    aircraft.aircraft_configuration?.model ??
    aircraft.model ??
    ""
  ).trim();
}

function firstJobId(lines: InvoiceLines[] | null | undefined): number | null {
  if (!lines?.length) return null;
  for (const line of lines) {
    const id = Number(line.job_id);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

async function fetchJob(jobId: number): Promise<IJob | null> {
  try {
    const resp = await axios.get<IJob>(`/v6/jobs/${jobId}`, {
      id: `get-invoice-pdf-job-${jobId}`,
    });
    axios.storage.remove(`get-invoice-pdf-job-${jobId}`);
    return resp.data ?? null;
  } catch {
    return null;
  }
}

async function fetchAircraft(
  aircraftId: unknown
): Promise<IAircraftDetail | null> {
  const id = Number.parseInt(String(aircraftId ?? "0"), 10);
  if (!id) return null;
  try {
    const resp = await axios.get<IAircraftDetail>(`/v6/aircraft/${id}`, {
      id: `get-invoice-pdf-aircraft-${id}`,
    });
    axios.storage.remove(`get-invoice-pdf-aircraft-${id}`);
    return resp.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Prepare stationery context for invoice/invoice.html.
 * Keeps DB total/gst (legacy PML uses invoices.total) and adds aircraft
 * header fields from the linked work order — same shape as WO estimate.
 */
export async function prepareInvoicePdfContext(
  invoice: InvoiceDetails
): Promise<Record<string, unknown>> {
  const context = cloneDeep(invoice) as unknown as Record<string, unknown> & {
    lines?: InvoiceLines[] | null;
    billing_date?: unknown;
    date_bill?: unknown;
    gst_total?: unknown;
    total_tax?: unknown;
  };

  if (context.lines) {
    context.lines = context.lines.filter((line) => line.active);
  }

  // API returns field names (by_alias=False): billing_date, total_tax.
  // Template expects billing_date; keep gst_total alias for older callers.
  if (context.billing_date == null && context.date_bill != null) {
    context.billing_date = context.date_bill;
  }
  if (context.gst_total == null && context.total_tax != null) {
    context.gst_total = context.total_tax;
  }

  // Do NOT recompute total from sum(sell) — that ignores qty and line types
  // not shown in PARTS/LABOUR/FREIGHT, and caused wrong Amount Due.

  const jobId = firstJobId(context.lines);
  let workOrderNo = jobId != null ? String(jobId) : "";
  let model = "";
  let serialNo = "";
  let aircraftReg = "";
  let totalTime = "";

  if (jobId != null) {
    const job = await fetchJob(jobId);
    if (job) {
      workOrderNo = String(job.name?.trim() || job.id || jobId);
      const aircraft = await fetchAircraft(job.aircraft_id);
      model = resolveAircraftModel(aircraft);
      serialNo = (aircraft?.serial_no ?? "").trim();
      aircraftReg = (aircraft?.registration ?? "").trim();
      const rawTt = (aircraft as IAircraftDetail & { total_time?: number | null })
        ?.total_time;
      totalTime = formatAircraftTotalTime(rawTt);
    }
  }

  context.work_order_no = workOrderNo;
  context.model = model;
  context.serial_no = serialNo;
  context.aircraft_serial = serialNo;
  context.aircraft_reg = aircraftReg;
  context.registration = aircraftReg;
  context.total_time = totalTime;

  return context;
}

/**
 * Build Invoice PDF blob using stationery-v2 payload shape.
 */
export async function buildInvoicePdfViaStationeryV2(args: {
  invoiceId: number;
}): Promise<InvoicePdfV2BuildResult> {
  const { invoiceId } = args;

  const resp = await axios.get<InvoiceDetails>(`/v6/invoice/${invoiceId}`, {
    id: `get-invoice-${invoiceId}-v2-fallback`,
  });
  axios.storage.remove(`get-invoice-${invoiceId}-v2-fallback`);

  const context = await prepareInvoicePdfContext(resp.data);

  const blob = await renderTemplatePreviewBlob(
    "invoice/invoice.html",
    "pdf",
    context
  );
  return { blob, filename: `invoice-${invoiceId}.pdf` };
}
